import {AfterViewInit, Component, HostListener, OnInit} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {UserService} from './domain/user/service/user.service';
import {NGXLogger} from 'ngx-logger';
import {User} from './domain/user/model/user';
import {NavigationEnd, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {MediaChange, MediaObserver} from '@ngbracket/ngx-layout';
import {Subject} from 'rxjs';
import {NotificationService} from './domain/notification/notification.service';
import {AnalyticsService} from './shared/analytics/analytics.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, AfterViewInit {
  mobile = true;
  enableRefresh = true;
  isLogin: boolean = false;
  targetElement: Element | undefined;
  currentUrl: string = '/';

  get seamlessInfoHeader(): boolean {
    const path = this.currentUrl.split('?')[0];
    if (path === '/info/api') {
      return !this.mobile;
    }

    return path === '/info/my-house' || path === '/info/open-house';
  }

  constructor(private readonly log: NGXLogger,
              private readonly media: MediaObserver,
              private readonly router: Router,
              private readonly matDialog: MatDialog,
              private readonly userService: UserService,
              private readonly notificationService: NotificationService,
              private readonly analytics: AnalyticsService,
              private readonly title: Title,
              private readonly meta: Meta) {
    this.analytics.initialize();
    this.media.asObservable().subscribe((change: MediaChange[]) => {
      this.mobile = change[0].mqAlias === 'xs';
    });

    this.userService.changeUser()
      .subscribe((user: User | undefined) => {
        this.isLogin = user !== undefined;
        this.analytics.setUserType(this.isLogin ? 'member' : 'guest');
      });

    // 새로고침 시 세션 확인은 헤더 상태만 갱신하므로 렌더링된 페이지 전체를 가리지 않는다.
    this.userService.checkSession(true)
      .subscribe({next: result => {
        this.isLogin = result;
        this.analytics.setUserType(result ? 'member' : 'guest');
        this.checkCountNotifications();
      }, error: () => {
        this.analytics.setUserType('unknown');
      }});

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.log.debug('Changed Url: ', event.url)
        if (this.isLogin) {
          this.checkCountNotifications();
        }

        this.currentUrl = event.urlAfterRedirects;
        this.updateCanonicalUrl(event.urlAfterRedirects);
      }
    });
  }

  @HostListener('window:scroll', ['$event.target'])
  onWindowScroll() {
    this.enableRefresh = window.scrollY <= 0
  }

  ngOnInit() {
    this.targetElement = document.querySelector('app-root')!;
  }

  myRefreshEvent(event: Subject<any>) {
    const previousUrl = this.router.url;

    this.router.navigateByUrl('/refresh', {skipLocationChange: true})
      .then(() => {
        const refreshTimer = setTimeout(() => {
          this.router.navigateByUrl(previousUrl)
            .then(() => {
              event.next('');
              clearTimeout(refreshTimer);
            });
        }, 300);
      });
  }

  ngAfterViewInit(): void {
    this.matDialog.afterOpened
      .subscribe(() => {
        this.log.debug('Dialog After Opened');
        if (this.mobile) {
          this.enableRefresh = false;
        }
      });

    this.matDialog.afterAllClosed
      .subscribe(() => {
        this.log.debug('Dialog After Closed');
        if (this.mobile) {
          this.enableRefresh = true;
        }
      });
  }

  private checkCountNotifications() {
    if (this.isLogin) {
      this.notificationService.countNotifications(true)
        .subscribe(() => {
        })
    }
  }

  private updateCanonicalUrl(url: string) {
    const parsedUrl = new URL(url, 'https://checkteam.co.kr');
    const path = parsedUrl.pathname || '/';
    const isNoticeDetail = /^\/info\/(?:events|notice|update)\/detail$/.test(path);
    const noticeId = isNoticeDetail ? parsedUrl.searchParams.get('id') : null;
    const canonicalPath = noticeId ? `${path}?id=${encodeURIComponent(noticeId)}` : path;
    const canonicalUrl = new URL(canonicalPath, 'https://checkteam.co.kr').href;
    const metadata = PAGE_METADATA[path] ?? DEFAULT_METADATA;
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const openGraphUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    const twitterUrl = document.querySelector<HTMLMetaElement>('meta[property="twitter:url"]');

    canonical?.setAttribute('href', canonicalUrl);
    openGraphUrl?.setAttribute('content', canonicalUrl);
    twitterUrl?.setAttribute('content', canonicalUrl);

    this.title.setTitle(metadata.title);
    this.meta.updateTag({name: 'description', content: metadata.description});
    this.meta.updateTag({name: 'robots', content: metadata.robots ?? DEFAULT_ROBOTS});
    this.meta.updateTag({property: 'og:title', content: metadata.title});
    this.meta.updateTag({property: 'og:description', content: metadata.description});
    this.meta.updateTag({name: 'twitter:title', content: metadata.title});
    this.meta.updateTag({name: 'twitter:description', content: metadata.description});
  }
}

interface PageMetadata {
  title: string;
  description: string;
  robots?: string;
}

const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const DEFAULT_METADATA: PageMetadata = {
  title: '체크팀 | 부동산 권리분석·실시간 등기변경 알림',
  description: '부동산 권리분석부터 실시간 등기 변경 알림까지! 체크팀에서 전세사기를 예방하고 소중한 보증금과 자산을 안전하게 지키세요.'
};

const PAGE_METADATA: Record<string, PageMetadata> = {
  '/info/owner-monitoring': {title: '소유자 변경 모니터링 소개 | 체크팀', description: '기업회원의 관리 부동산에 대한 월별 소유자 변경 내역과 등본을 한곳에서 확인하는 서비스입니다.', robots: 'noindex, follow'},
  '/owner-monitoring': {title: '소유자 변경 모니터링 | 체크팀', description: '회차별 소유자 변경 보고서와 첨부 등본을 확인하는 기업회원 전용 화면입니다.', robots: 'noindex, follow'},
  '/analysis': {title: '부동산 권리분석 시작 | 체크팀', description: '주소와 등기 정보를 바탕으로 부동산 권리관계와 보증금·담보 위험을 점검하는 체크팀 권리분석을 시작하세요.'},
  '/analysis/info': {title: '부동산 권리분석 서비스 | 등기·시세·선순위 위험 분석 | 체크팀', description: '체크팀 부동산 권리분석은 등기사항증명서, 실거래가·AI 시세, 선순위 권리와 채권금액을 함께 비교해 전세·담보·매매 위험을 확인하는 서비스입니다.'},
  '/info/business-analysis': {title: '사업자 권리분석 소개 | 체크팀', description: '기업 내부 심사를 위한 프라이빗과 고객 신청·심사 연동을 위한 브릿지 권리분석의 기능과 도입 방법을 확인하세요.'},
  '/info/business-analysis-preview': {title: '사업자 권리분석 미리보기 | 체크팀', description: '예시 데이터로 프라이빗과 브릿지 권리분석의 이용 화면, 기업 내부 심사와 고객 신청·결과 안내 흐름을 확인하세요.'},
  '/info/business-analysis-private': {title: '프라이빗 권리분석 | 사업자 서비스 | 체크팀', description: '기업의 심사 기준과 보고서 양식에 맞춘 전용 권리분석 환경으로 반복되는 내부 분석 업무를 자동화합니다.'},
  '/info/business-analysis-bridge': {title: '브릿지 권리분석 | 사업자 서비스 | 체크팀', description: '고객 신청정보 접수부터 사업자 기준의 권리분석·심사와 결과 안내까지 연결하는 서비스입니다.'},
  '/info/my-house': {title: '부동산 등기변경 알림 서비스 | 소유권·근저당·압류 변동 확인 | 체크팀', description: '체크팀 부동산 등기변경 알림은 소유권 이전, 근저당권 설정·말소, 압류·가압류 등 중요 변동을 확인하고 카카오톡·SMS·이메일·앱으로 안내하는 서비스입니다.'},
  '/info/open-house': {title: '오픈하우스 등기변경 알림 | 체크팀', description: '관심 부동산의 등기 변경을 지속적으로 확인하고 위험 신호를 받아보는 체크팀 오픈하우스 서비스입니다.'},
  '/info/company': {title: '회사 소개 | 체크팀', description: '부동산 정보의 비대칭을 줄이고 더 안전한 거래를 만드는 체크팀의 비전과 서비스를 소개합니다.'},
  '/info/faq': {title: '자주 묻는 질문 | 체크팀', description: '권리분석, 등기변경 알림, 결제와 이용 방법 등 체크팀 서비스에 대한 자주 묻는 질문과 답변입니다.'},
  '/scorer': {title: 'AI 부동산 시세 예측·실거래가 검색 | 체크팀', description: '주소를 검색해 AI 예측 시세와 최신 매매·전세·월세 실거래가를 비교하고, 향후 2년의 월별 가격 전망을 확인하세요.'},
  '/info/scorer': {title: 'AI 부동산 시세 예측 | 체크팀 스코어', description: '주소와 주택 정보를 바탕으로 AI 예측 시세와 실거래 데이터를 비교해 보는 체크팀 스코어 서비스입니다.'},
  '/info/api': {title: '부동산 데이터 API | 체크팀', description: '등기, 권리분석과 부동산 데이터를 서비스에 연결할 수 있는 체크팀 API의 기능과 도입 방법을 확인하세요.'},
  '/info/pricing': {title: '서비스 요금 안내 | 체크팀', description: '체크팀 권리분석과 등기변경 알림 서비스의 이용 요금과 제공 기능을 한눈에 확인하세요.'},
  '/info/events': {title: '이벤트 | 체크팀', description: '체크팀의 최신 이벤트와 혜택, 서비스 소식을 확인하세요.'},
  '/info/notice': {title: '공지사항 | 체크팀', description: '체크팀 서비스 운영과 이용에 필요한 주요 공지사항을 확인하세요.'},
  '/info/update': {title: '서비스 업데이트 | 체크팀', description: '체크팀의 새로운 기능과 서비스 개선 내용을 확인하세요.'},
  '/info/notice/detail': {title: '체크팀 소식 상세 | 체크팀', description: '체크팀 서비스의 공지사항과 업데이트 상세 내용을 확인하세요.'},
  '/info/update/detail': {title: '체크팀 업데이트 상세 | 체크팀', description: '체크팀 서비스의 기능 업데이트와 개선 내용을 확인하세요.'},
  '/info/events/detail': {title: '체크팀 이벤트 상세 | 체크팀', description: '체크팀에서 진행하는 이벤트와 혜택의 상세 내용을 확인하세요.'},
  '/real-estate-calculator': {title: '부동산 계산기 | 체크팀', description: '취득세, 중개보수, 대출이자, LTV·DTI·DSR과 전월세 전환 등 부동산 의사결정에 필요한 계산기를 이용하세요.'},
  '/terms/privacy': {title: '개인정보처리방침 | 체크팀', description: '체크팀 서비스의 개인정보 수집·이용·보관 및 이용자 권리에 관한 처리방침을 확인하세요.'},
  '/my-real-estate': {title: '내 부동산 대시보드 | 체크팀', description: '등록 부동산과 권리분석, 등기변경 및 AI 시세 현황을 확인하는 체크팀 회원 전용 화면입니다.', robots: 'noindex, follow'},
  '/house/open': {title: '관심 부동산 관리 | 체크팀', description: '관심 부동산의 등기변경 확인 현황을 관리하는 체크팀 서비스 화면입니다.', robots: 'noindex, follow'}
};
